import { createContext, useContext, useMemo, useState } from 'react';
import { Member } from 'src/modules/member/models/member';

export interface MemberContextSettings {
  createMode: boolean;
  setCreateMode: (state: boolean) => void;
  editMode: boolean;
  setEditMode: (state: boolean) => void;
  composeEmailMode: boolean;
  setComposeEmailMode: (state: boolean) => void;
  createInvoiceMode: boolean;
  setCreateInvoiceMode: (state: boolean) => void;
  editMember: Member | undefined;
  setEditMember: (member: Member | undefined) => void;
  selectedMembers: Member[] | [];
  setSelectedMembers: (members: Member[] | []) => void;
}

export const MemberContext = createContext<MemberContextSettings>({
  createMode: false,
  setCreateMode: () => undefined,
  editMode: false,
  setEditMode: () => undefined,
  composeEmailMode: false,
  setComposeEmailMode: () => undefined,
  createInvoiceMode: false,
  setCreateInvoiceMode: () => undefined,
  editMember: undefined,
  setEditMember: () => undefined,
  selectedMembers: [],
  setSelectedMembers: () => undefined,
});

export const useMemberContext = () => {
  const context = useContext(MemberContext);
  if (context === undefined) {
    throw new Error('useMemberContext must be within AppContext');
  }
  return context;
};

interface Props {
  children: JSX.Element;
}
export const MemberContextProvider = ({ children }: Props) => {
  const [createMode, setCreateMode] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [composeEmailMode, setComposeEmailMode] = useState<boolean>(false);
  const [createInvoiceMode, setCreateInvoiceMode] = useState<boolean>(false);
  const [editMember, setEditMember] = useState<Member>();
  const [selectedMembers, setSelectedMembers] = useState<Member[] | []>([]);

  const memberContext = useMemo(
    () => ({
      createMode,
      setCreateMode,
      editMode,
      setEditMode,
      composeEmailMode,
      setComposeEmailMode,
      createInvoiceMode,
      setCreateInvoiceMode,
      editMember,
      setEditMember,
      selectedMembers,
      setSelectedMembers,
    }),
    [createMode, editMode, composeEmailMode, createInvoiceMode, editMember, selectedMembers],
  );

  return <MemberContext.Provider value={memberContext}>{children}</MemberContext.Provider>;
};
